import React from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import classNames from './EditAmendment.module.css';
import Page from 'Common/Layout/Page';
import Section from 'Common/Layout/Section';
import ActionButton from 'Common/Buttons/ActionButton';
import AsyncList from 'Common/Components/AsyncList/AsyncList';
import SuccessFailure from 'Common/Components/success-failure/SuccessFailure.container';
import ConfirmCancel from 'Common/Components/ConfirmModal/ConfirmModal';
import { makeStyles } from 'utils';

const styles = makeStyles(classNames);

function EditAmendmentView({
    title,
    allowSave,
    amendmentNumber,
    amendmentDate,
    errorMessage,
    reasonsDataSource,
    reasonsSelected,
    showCancelConfirmation,
    validationErrors = {},
    onCancel,
    onCancelClick,
    onDateChange,
    onReasonsChange,
    onReasonsLoad,
    onSaveClick
}) {
    return (
        <Page title={title}>
            <Section width="small">
                <Paper elevation={2} className={styles('form__container')}>
                    <div className={styles('form__content')}>
                        <div style={{ gridColumnStart: 1 }} className={styles('form__control', 'form__control--top')}>
                            <TextField
                                label="Amendment Number"
                                value={amendmentNumber}
                                InputProps={{ readOnly: true }}
                            />
                        </div>
                        <div style={{gridColumnStart: 1 }} className={styles('form__control')}>
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <KeyboardDatePicker
                                    variant="inline"
                                    label="Amendment Date"
                                    error={!!validationErrors.amendmentDate}
                                    helperText={validationErrors.amendmentDate}
                                    disablePast
                                    autoOk
                                    format="dd/MM/yyyy"
                                    value={amendmentDate}
                                    onChange={onDateChange}
                                />
                            </MuiPickersUtilsProvider>
                        </div>
                        <div style={{gridColumnStart: 2, gridRowStart: 1, gridRowEnd: 'span 2' }} className={styles('form__control', 'form__control--top')}>
                            <AsyncList
                                dataSource={reasonsDataSource}
                                label="Reasons"
                                keyField="questionId"
                                labelField="text"
                                error={!!validationErrors.reasons}
                                helperText={validationErrors.reasons}
                                selected={reasonsSelected}
                                onChange={onReasonsChange}
                                onLoad={onReasonsLoad}
                            />
                        </div>
                    </div>
                </Paper>
            </Section>
            {errorMessage && (
                <Section>
                    <SuccessFailure hasResult={true} result={false} successMessage="" failMessage={errorMessage} />
                </Section>
            )}
            <Section variant="commands">
                <Section.Command>
                    <ActionButton text="Cancel" onClick={onCancelClick} />
                </Section.Command>
                <Section.Command align="right">
                    <ActionButton text="Save Changes" variant="primary" disabled={!allowSave} onClick={onSaveClick} />
                </Section.Command>
            </Section>
            {showCancelConfirmation && (
                <ConfirmCancel
                    isOpen={true}
                    heading="Cancel"
                    message="Your changes have not been saved. Are you sure you want to cancel?"
                    proceedButton="Yes"
                    withdrawButton="No"
                    onProceed={() => onCancel(true)}
                    onWithdraw={() => onCancel(false)}
                />
            )}
        </Page>
    );
}

EditAmendmentView.propTypes = {
    title: PropTypes.string,
    allowSave: PropTypes.bool,
    amendmentNumber: PropTypes.number.isRequired,
    amendmentDate: PropTypes.instanceOf(Date).isRequired,
    errorMessage: PropTypes.string,
    reasonsDataSource: PropTypes.any,
    reasonsSelected: PropTypes.array,
    showCancelConfirmation: PropTypes.bool,
    validationErrors: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    onCancelClick: PropTypes.func.isRequired,
    onDateChange: PropTypes.func.isRequired,
    onReasonsChange: PropTypes.func.isRequired,
    onReasonsLoad: PropTypes.func.isRequired,
    onSaveClick: PropTypes.func.isRequired,
};

export default React.memo(EditAmendmentView);